import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X, Quote, AlertTriangle, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API } from "../lib/api";

const FILTERS = [["all", "All"], ["recovery", "Recovery"], ["whanau", "Whānau"], ["youth", "Youth"], ["community", "Community"], ["personal", "Personal"]];
const ROLES = ["Lived experience", "Whānau voice", "Frontline worker", "Community member", "Other"];

function Attachment({ story }) {
  if (!story.attachment_path) return null;
  const src = `${API}/files/${story.attachment_path}`;
  if (story.attachment_type?.startsWith("audio/")) {
    return <audio controls src={src} className="w-full mt-4 h-10" data-testid="story-audio-player" />;
  }
  if (story.attachment_type?.startsWith("image/")) {
    return <img src={src} alt={story.attachment_name || "Story attachment"} className="mt-4 rounded-lg max-h-56 object-cover" />;
  }
  return <a href={src} target="_blank" rel="noreferrer" className="mt-4 inline-block text-xs text-[#EF4444] underline">{story.attachment_name || "View attachment"}</a>;
}

export default function Stories() {
  const [stories, setStories] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ name: "", anonymous: false, role: ROLES[0], title: "", story: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/stories").then((r) => setStories(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let attachment = {};
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        attachment = { attachment_path: up.data.path, attachment_name: up.data.filename, attachment_type: up.data.content_type };
      }
      const { data } = await api.post("/stories", { ...form, ...attachment });
      toast.success(data.urgent
        ? "Ngā mihi. Your story has been received and flagged for priority review. If you need support now, free call/text 1737."
        : "Ngā mihi — your story has been received and will be reviewed before publishing.");
      setOpen(false);
      setFile(null);
      setForm({ name: "", anonymous: false, role: ROLES[0], title: "", story: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const visible = stories.filter((s) => filter === "all" || s.theme === filter);

  return (
    <section id="stories" className="bg-[#0B0C10] py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6">
        <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mono-label text-[#EF4444] mb-4">Lived experience · guidance · understanding</motion.p>
        <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="font-display font-extrabold uppercase text-3xl sm:text-5xl text-white tracking-tight mb-4">
          Voices that have<br />been there
        </motion.h2>
        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-slate-400 max-w-2xl mb-8">
          People who have seen and lived the chaotic lifestyle — and now want more for themselves, their children, their whānau and their communities. We are so much more than being influenced by drug harm.
        </motion.p>

        <div className="flex gap-2 flex-wrap mb-10">
          {FILTERS.map(([key, label]) => (
            <button key={key} data-testid={`story-filter-${key}`} onClick={() => setFilter(key)}
              className={`rounded-full px-4 py-1.5 text-xs font-bold transition-all ${filter === key ? "bg-white text-black" : "border border-[#212636] text-slate-400 hover:text-white"}`}>
              {label}
            </button>
          ))}
          <button data-testid="open-submit-story-modal" onClick={() => setOpen(true)}
            className="ml-auto rounded-full px-5 py-1.5 text-xs font-bold bg-[#EF4444] hover:bg-[#DC2626] text-white transition-colors">
            Share your story
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {visible.map((s, i) => (
            <motion.article key={s.id} data-testid={`lived-experience-card-${i + 1}`}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 2) * 0.1 }}
              className="bg-[#12141C] border border-[#212636] rounded-2xl p-7 hover:border-slate-500 transition-colors">
              <Quote size={20} className="text-[#EF4444] mb-4" />
              <h3 className="font-display font-bold text-white text-xl mb-3">{s.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed whitespace-pre-line">
                {expanded === s.id ? s.story : s.story.slice(0, 220) + (s.story.length > 220 ? "…" : "")}
              </p>
              {s.story.length > 220 && (
                <button data-testid={`story-expand-${i + 1}`} onClick={() => setExpanded(expanded === s.id ? null : s.id)} className="mt-3 text-xs font-bold text-[#EF4444] hover:text-white transition-colors">
                  {expanded === s.id ? "Close" : "Read the full journey"}
                </button>
              )}
              <Attachment story={s} />
              <div className="mt-5 pt-4 border-t border-[#212636] flex items-center justify-between">
                <span className="text-xs text-slate-500 font-grotesk">{s.name} · {s.role}</span>
                <span className="mono-label text-slate-600">{s.theme}</span>
              </div>
            </motion.article>
          ))}
        </div>
        {visible.length === 0 && <p className="text-slate-500 text-sm">No stories in this theme yet — yours could be the first.</p>}
      </div>

      {open && (
        <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.96, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
            className="bg-[#12141C] border border-[#212636] rounded-2xl p-7 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-1">
              <h3 className="font-display font-bold text-white text-xl">Share your story</h3>
              <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-white" aria-label="Close"><X size={20} /></button>
            </div>
            <p className="text-xs text-slate-500 mb-5 leading-relaxed">
              Safe and confidential. Every story is reviewed by our team before it appears. If you're in crisis right now, please free call or text <a href="tel:1737" className="text-[#EF4444] font-bold">1737</a> first — your story matters, and so do you.
            </p>
            <form data-testid="community-story-form" onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} disabled={form.anonymous}
                  placeholder="First name or initials" data-testid="story-input-name"
                  className="bg-[#090A0F] border border-[#212636] rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#EF4444] disabled:opacity-40" />
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="story-select-role"
                  className="bg-[#090A0F] border border-[#212636] rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#EF4444]">
                  {ROLES.map((r) => <option key={r}>{r}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                <input type="checkbox" checked={form.anonymous} onChange={(e) => setForm({ ...form, anonymous: e.target.checked })}
                  data-testid="story-anonymous-checkbox" className="accent-[#EF4444]" />
                Publish anonymously
              </label>
              <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Give your story a title"
                data-testid="story-input-title" maxLength={140}
                className="w-full bg-[#090A0F] border border-[#212636] rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#EF4444]" />
              <textarea required minLength={20} value={form.story} onChange={(e) => setForm({ ...form, story: e.target.value })} rows={6}
                placeholder="Tell it your way — what happened, what it cost, what changed…" data-testid="story-input-body"
                className="w-full bg-[#090A0F] border border-[#212636] rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#EF4444]" />
              <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer border border-dashed border-[#212636] rounded-lg px-3 py-2.5 hover:border-slate-500 transition-colors">
                <Paperclip size={13} />
                {file ? file.name : "Attach a voice note, photo or document (optional, max 15 MB)"}
                <input type="file" accept="audio/*,image/jpeg,image/png,image/webp,application/pdf" className="hidden"
                  data-testid="story-input-file" onChange={(e) => setFile(e.target.files[0] || null)} />
              </label>
              <p className="text-[11px] text-slate-600 flex gap-1.5"><AlertTriangle size={12} className="shrink-0 mt-0.5" /> Please don't name other people or share their personal details. We handle your story under our privacy commitments.</p>
              <button type="submit" disabled={busy} data-testid="story-submit-button"
                className="w-full bg-[#EF4444] hover:bg-[#DC2626] disabled:opacity-50 transition-colors text-white font-bold rounded-lg py-3 text-sm">
                {busy ? "Sending…" : "Send for review"}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </section>
  );
}
